import { useEffect, useRef, useState } from "react";

import { EXEC_KPI_PAGE_SIZE } from "./execKpi.mjs";
import { MAX_PAGES, fetchAllPages } from "./execKpiFetch.mjs";
import { RO_GROUP_COLUMNS, expandGroupedRows } from "./roEfficiency.mjs";
import type { RoRow } from "./roEfficiency.d.mts";
import { LiveCache, useLiveCacheVersion } from "./liveCacheHook";
import { getSupabase } from "./supabase";
import { scopeTenant } from "./tenantScope.mjs";
import { useTenantId } from "./tenant";

/**
 * RO Efficiency tab: the one read every panel shares.
 *
 * The panels do the client tracker's arithmetic on render
 * (roEfficiency.mjs), so they need the work order rows themselves, not an
 * aggregate: which driver, which day, which work type, which status. The
 * read is one tenant-scoped service_event slice per (lines, window, work
 * type list), paged past PostgREST's 1,000-row cap with an explicit total
 * order (fetchAllPages, the Executive page's loop), projected to the eight
 * columns the arithmetic reads plus site_name (the tracker's cell detail
 * lists the customer site of each job). Panels asking for the same slice
 * share one in-flight request through the module cache below, so the four
 * roll off panels on the tab cost one read, not four.
 *
 * If MAX_PAGES come back full the result carries `truncated` and the panel
 * says so on its face; a partial month is never shown as a whole one.
 *
 * GROUPED SLICES (the RO Efficiency report). A year of roll off work orders
 * read row by row is 19 sequential pages, and every page past the fifth
 * costs about three seconds for a tenant member (the RLS filter runs per row
 * and a deep OFFSET re-reads every row before it): about 45 s before the
 * grid. The report instead reads one calendar month per request, grouped by
 * the columns the arithmetic reads (RO_GROUP_COLUMNS) with a count, so a
 * month is one page of about 700 groups instead of three pages of 2,100
 * rows, and never a deep offset. Up to SLICE_CONCURRENCY slices are in
 * flight at once, in the order the caller lists them (the report asks for
 * the newest month first, so the default grid is ready first). The groups
 * are expanded back into rows (expandGroupedRows), so the arithmetic runs on
 * the same rows it always did. Settled slices stay in the module cache for
 * the page load: switching tabs and back redraws with no read.
 *
 * LIVE REFRESH (AJ 2026-09-30). refreshRoEfficiency re-reads every grouped
 * slice already read for the tenant and swaps them in together
 * (LiveCache.refresh); the hooks redraw from the new answers without a
 * loading state. Row-level slices (a clicked day's detail) are dropped and
 * re-read on the next click.
 */

export const RO_TABLE = "service_event";

export const RO_COLUMNS =
  "event_id,event_date,division,driver_id,route_id,work_type,completion_status,equipment_type,site_name";

export interface RoSlice {
  equipmentTypes: string[];
  /** Inclusive 'YYYY-MM-DD' bounds on the scheduled date (event_date). */
  from: string;
  to: string;
  /** Optional work type allow-list, applied server-side. */
  workTypes?: string[];
}

export interface RoFetchResult {
  rows: RoRow[];
  truncated: boolean;
}

export interface RoRowsState {
  rows: RoRow[];
  truncated: boolean;
  isLoading: boolean;
  error: Error | null;
}

const cache = new LiveCache<RoFetchResult>();
/** Grouped slices; settled answers (peek) let a remount draw them at once. */
const groupedCache = new LiveCache<RoFetchResult>();

/** Grouped slices in flight at once, across every caller. */
export const SLICE_CONCURRENCY = 6;
/** Pages per grouped slice; a month is about 700 groups, one page. */
const GROUP_MAX_PAGES = 20;

function sliceKey(tenantId: string, slice: RoSlice): string {
  return JSON.stringify([
    tenantId,
    [...slice.equipmentTypes].sort(),
    slice.from,
    slice.to,
    slice.workTypes ? [...slice.workTypes].sort() : null,
  ]);
}

export async function fetchRoRows(tenantId: string, slice: RoSlice): Promise<RoFetchResult> {
  const supabase = getSupabase();
  const result = await fetchAllPages<RoRow>(
    (from, to) => {
      let q = scopeTenant(supabase.from(RO_TABLE).select(RO_COLUMNS), tenantId)
        .in("equipment_type", slice.equipmentTypes)
        .gte("event_date", slice.from)
        .lte("event_date", slice.to);
      if (slice.workTypes && slice.workTypes.length > 0) q = q.in("work_type", slice.workTypes);
      return q
        .order("event_date", { ascending: true })
        .order("event_id", { ascending: true })
        .range(from, to);
    },
    { pageSize: EXEC_KPI_PAGE_SIZE, maxPages: MAX_PAGES },
  );
  return { rows: result.rows, truncated: result.truncated };
}

/**
 * One slice as grouped rows, expanded back to one row per work order
 * (without event_id or site_name, which only the cell detail reads).
 */
export async function fetchRoGrouped(tenantId: string, slice: RoSlice): Promise<RoFetchResult> {
  const supabase = getSupabase();
  const select = `${RO_GROUP_COLUMNS.join(",")},event_id.count(),first_event_id:event_id.min()`;
  const result = await fetchAllPages<Record<string, unknown>>(
    (from, to) => {
      let q: any = scopeTenant((supabase.from(RO_TABLE) as any).select(select), tenantId)
        .in("equipment_type", slice.equipmentTypes)
        .gte("event_date", slice.from)
        .lte("event_date", slice.to);
      if (slice.workTypes && slice.workTypes.length > 0) q = q.in("work_type", slice.workTypes);
      // Every group column in the order: stable pages when a slice runs past one.
      for (const col of RO_GROUP_COLUMNS) q = q.order(col, { ascending: true, nullsFirst: true });
      return q.range(from, to);
    },
    { pageSize: EXEC_KPI_PAGE_SIZE, maxPages: GROUP_MAX_PAGES },
  );
  return { rows: expandGroupedRows(result.rows), truncated: result.truncated };
}

let inFlight = 0;
const waiting: (() => void)[] = [];

async function acquire(): Promise<void> {
  if (inFlight < SLICE_CONCURRENCY) {
    inFlight += 1;
    return;
  }
  await new Promise<void>((resolve) => waiting.push(resolve));
}

function release(): void {
  // Hand the slot straight to the next waiter so no newcomer can overtake it.
  const next = waiting.shift();
  if (next) next();
  else inFlight -= 1;
}

function cachedGrouped(tenantId: string, slice: RoSlice): Promise<RoFetchResult> {
  return groupedCache.get(sliceKey(tenantId, slice), async () => {
    await acquire();
    try {
      return await fetchRoGrouped(tenantId, slice);
    } finally {
      release();
    }
  });
}

/**
 * Live refresh: re-read the tenant's grouped slices, swap them in together;
 * drop its row-level slices. Rejects (keeping the old data) on any failure.
 */
export async function refreshRoEfficiency(tenantId: string): Promise<void> {
  await groupedCache.refreshTenant(tenantId);
  cache.invalidate((key) => key.includes(tenantId));
}

/** Row-level work orders of one slice (event_id and site_name included), cached. */
export function loadRoRows(tenantId: string, slice: RoSlice): Promise<RoFetchResult> {
  return cachedRoRows(tenantId, slice);
}

function cachedRoRows(tenantId: string, slice: RoSlice): Promise<RoFetchResult> {
  // A failed read is not cached: the next mount retries it.
  return cache.get(sliceKey(tenantId, slice), () => fetchRoRows(tenantId, slice));
}

/** Test seam: forget every cached slice. */
export function __resetRoRowsCache(): void {
  cache.clear();
  groupedCache.clear();
}

export interface RoSlicesState {
  /** One entry per slice, in the order given; null until that slice is read. */
  results: (RoFetchResult | null)[];
  error: Error | null;
}

function settledFor(tenantId: string | null, slices: RoSlice[] | null): RoSlicesState {
  return {
    results: (slices ?? []).map((s) =>
      tenantId ? (groupedCache.peek(sliceKey(tenantId, s)) ?? null) : null,
    ),
    error: null,
  };
}

/**
 * Several grouped slices for the route's tenant, each landing as soon as it
 * is read (the caller draws what it can while the rest arrive). Slices are
 * requested in the order given. A null list runs no read.
 */
export function useRoGroupedSlices(slices: RoSlice[] | null): RoSlicesState {
  const tenantId = useTenantId();
  const [state, setState] = useState<RoSlicesState>(() => settledFor(tenantId, slices));
  const key = slices && tenantId ? JSON.stringify(slices.map((s) => sliceKey(tenantId, s))) : null;
  // A live refresh swapped new slices in: redraw from them (already settled).
  const version = useLiveCacheVersion(groupedCache);

  useEffect(() => {
    // Fail closed until the slug resolves to a tenant: an unscoped read would
    // blend tenants for a staff session.
    if (!slices || !tenantId) {
      setState({ results: (slices ?? []).map(() => null), error: null });
      return;
    }
    let cancelled = false;
    setState(settledFor(tenantId, slices));
    slices.forEach((slice, i) => {
      cachedGrouped(tenantId, slice).then(
        (res) => {
          if (cancelled) return;
          setState((s) => {
            if (s.results[i] === res) return s;
            const results = [...s.results];
            results[i] = res;
            return { ...s, results };
          });
        },
        (err: unknown) => {
          if (cancelled) return;
          setState((s) => ({
            ...s,
            error: s.error ?? (err instanceof Error ? err : new Error(String(err))),
          }));
        },
      );
    });
    return () => {
      cancelled = true;
    };
    // `key` captures every field of every slice and the tenant.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, version]);

  return state;
}

/** The rows of one slice for the route's tenant; null slice runs no read. */
export function useRoRows(slice: RoSlice | null): RoRowsState {
  const tenantId = useTenantId();
  const [state, setState] = useState<RoRowsState>({
    rows: [],
    truncated: false,
    isLoading: slice !== null,
    error: null,
  });
  const key = slice && tenantId ? sliceKey(tenantId, slice) : null;
  const version = useLiveCacheVersion(cache);
  const lastKey = useRef<string | null>(null);

  useEffect(() => {
    // A live refresh of the same slice keeps the old rows up until the new land.
    const sameSlice = key !== null && lastKey.current === key;
    lastKey.current = key;
    if (!slice) {
      setState({ rows: [], truncated: false, isLoading: false, error: null });
      return;
    }
    // Fail closed until the slug resolves to a tenant: an unscoped read would
    // blend tenants for a staff session.
    if (!tenantId) {
      setState({ rows: [], truncated: false, isLoading: true, error: null });
      return;
    }
    let cancelled = false;
    if (!sameSlice) setState((s) => ({ ...s, isLoading: true, error: null }));
    cachedRoRows(tenantId, slice).then(
      (res) => {
        if (!cancelled) {
          setState({ rows: res.rows, truncated: res.truncated, isLoading: false, error: null });
        }
      },
      (err: unknown) => {
        if (!cancelled) {
          setState({
            rows: [],
            truncated: false,
            isLoading: false,
            error: err instanceof Error ? err : new Error(String(err)),
          });
        }
      },
    );
    return () => {
      cancelled = true;
    };
    // `key` captures every field of `slice` and the tenant.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, version]);

  return state;
}
